import pollService from '@/services/API/poll/index';
import { apiService } from '@/services/APIService';
import { rServices } from '@/plugins/api';

import { rHandler } from '@/plugins/RHandler';
import { POLL_TYPES } from '@/modules/Poll/constants';

import JournalModel from './journal';
import BaseModel from './base';
import PollQuestion from './poll-question';

import ExpertiseCriteriaSet from './expertise-criteria-set';

import User from '@/utils/user.js';

export default class Poll extends BaseModel {
  #user;

  constructor(poll, url) {
    super(poll, url);
    this.responseData = poll;

    this.id = poll.id;
    this.name = poll.name;
    this.owner_id = poll.owner_id;
    this.description = poll.description;
    this.status = poll.status;
    this.type = poll.type;
    this.created_at = poll.created_at;
    this.updated_at = poll.updated_at;
    this.settings_id = poll.settings_id;
    this.settings = poll.settings || {};
    this.criterion_set_id = poll.criterion_set_id || null;
    this.set = poll.set ? new ExpertiseCriteriaSet(poll.set) : null;

    this.questions = (poll.questions || []).map((item) => new PollQuestion(item));
    this.poll_folders = poll.poll_folders || [];

    this.event_stages = poll.event_stages || [];

    this.hints = [];

    this.last_journal = this.getLastPollJournal();

    this.stages_linked = [];

    // search[is_fake]: 1,
    // expand: is_fake,
    this.is_fake = poll.is_fake || false;

    this.rights = null;

    this.#user = new User();
  }

  getLastPollJournal() {
    if (this.responseData.last_poll_journal) return new JournalModel(this.responseData.last_poll_journal);
    return null;
  }

  /**
   * Подгрузка настроек
   */
  fetchSettings(stageId = null) {
    const { showSettingsByStage, showPollSettings } = pollService;
    const requestData = stageId ? {
      method: showSettingsByStage,
      args: [stageId, this.id],
      serializer: (response) => {
        const [settings] = response?.data?.data || [];
        return settings;
      },
    } : {
      method: showPollSettings,
      args: [this.responseData.settings_id],
      serializer: (response) => response?.data?.data || {},
    };
    return rHandler(
      requestData.method(...requestData.args),
      (response) => {
        this.settings = requestData.serializer(response);
        return response;
      },
    );
  }

  /**
   * Остались ли попытки
   * @return boolean
   */
  get hasAttempts() {
    if (this.settings.attempts_count && this.last_journal) {
      return this.settings.attempts_count > this.last_journal.attempt;
    }
    return true;
  }

  /**
   * Количество оставшихся попыток
   *
   * @return number
   */
  get restAttempts() {
    return this.settings.attempts_count - (this.last_journal?.attempt || 0);
  }

  /**
   * Завершено ли проходение заданий
   *
   * @return number
   */
  get isFinishedStatus() {
    return this.last_journal?.isFinished;
  }

  /**
   * В процессе выполнения
   *
   * @return number
   */
  get isPassingStatus() {
    return this.last_journal && !this.last_journal.isFinished;
  }

  /**
   * Не начато
   *
   * @return number
   */
  get isWaitingStatus() {
    return !this.last_journal;
  }

  get isOwner() {
    return this.#user?.isAuthenticated && this.owner_id === this.#user?.data?.id;
  }

  // Тип опроса = автоматизированное
  get isAttachment() {
    return this.type === POLL_TYPES.ATTACHMENT;
  }

  // Тип опроса = смешанное
  get isTesting() {
    return this.type === POLL_TYPES.TESTING;
  }

  // Вернуть задание на доработку

  returnRefurbishment() {
    return rHandler(
      apiService.post(`/poll-answers/finish/${this.id}/undo`),
      () => { this.status = 0; },
      (resp) => new Error(resp),
    );
  }

  async getSettings(params = {}) {
    const { settings_id } = this;
    const { response, error } = await rServices.polls.showPollSettings({ args: [settings_id, params] });
    this.settings = response?.data?.data || {};
    return { ...response, ...error };
  }

  async updateSettings(settings) {
    const { settings_id } = this;
    const { response, error } = await rServices.polls.updatePollSettings({ args: [settings_id, settings] });
    if (!error) {
      this.settings = {
        ...this.settings,
        ...settings,
      };
    }
    return { ...response, ...error };
  }

  async linkToStage(settings) {
    const { event_stage_id } = settings;
    const { id, stages_linked } = this;
    if (stages_linked.includes(event_stage_id)) return {};
    const params = {
      ...settings,
      poll_id: id,
    };
    const { response, error } = await rServices.polls.linkPollToStage({ args: [event_stage_id, params] });
    if (!error) {
      this.stages_linked.push(event_stage_id);
    }
    return { ...response, ...error };
  }

  async getHints() {
    const { id } = this;
    const { response, error } = await rServices.polls.getPollHints({ args: [id] });
    this.hints = response?.data?.data || [];
    return { ...response, ...error };
  }

  setRights(list) {
    if (list?.length) {
      this.rights = list.find(({ poll_id }) => poll_id === this.id) || null;
    }
  }
}
